"use client";

import Chevron from "@/components/icons/Chevron";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import styles from "./cart-size-selector.module.css";

export type CartSize = {
  name: string;
  label: string;
  stock: boolean;
  fewLeft: boolean;
  itemId: string;
};

interface CartSizeSelectorProps {
  currentSize: string;
  sizes: CartSize[];
  onSizeChange: (itemId: string) => void;
  disabled?: boolean;
}

const CartSizeSelector = ({
  currentSize,
  sizes,
  onSizeChange,
  disabled = false,
}: CartSizeSelectorProps) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleSizeSelect = (itemId: string) => {
    onSizeChange(itemId);
    setOpen(false);
  };

  return (
    <div className={styles.wrapper} ref={dropdownRef}>
      <button
        className={clsx(styles.trigger, { [styles.disabled]: disabled })}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        type="button"
        disabled={disabled}
      >
        <span className={styles.label}>Size:</span>
        <span className={styles.value}>{currentSize}</span>
        <Chevron
          className={clsx(styles.arrow, { [styles.open]: open })}
          scale={1}
        />
      </button>

      {open && (
        <ul className={styles.dropdown}>
          {sizes.map((size) => (
            <li key={size.itemId} className={styles.option}>
              <button
                className={clsx(styles.optionButton, {
                  [styles.selected]: size.name === currentSize,
                  [styles.outOfStock]: !size.stock,
                })}
                onClick={() => size.stock && handleSizeSelect(size.itemId)}
                disabled={!size.stock}
                type="button"
              >
                <span className={styles.sizeName}>{size.name}</span>
                {!size.stock && (
                  <span className={styles.stockStatus}>Out of stock</span>
                )}
                {size.stock && size.fewLeft && (
                  <span className={styles.stockStatus}>Few left</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CartSizeSelector;
